import { useAuth } from "@/auth/auth";
import { useUser } from "@/client/backend/user";
import { PublicShell } from "@/components/layout/PublicShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToastAction } from "@/components/ui/toast";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import { formatErrorMessage } from "@/utils/format";
import { CheckCircledIcon, CheckIcon, ChevronDownIcon, ChevronRightIcon, CopyIcon, ReloadIcon } from "@radix-ui/react-icons";
import { useEffect, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";

const CLI_SERVER_PORT = 32412;
const CLI_CALLBACK_TIMEOUT_MS = 5000;

type CliDeliveryState = "idle" | "delivering" | "connected" | "manual";

interface CliSessionInfo {
  hostname: string;
  phrase: string;
  createdAt: string;
  expiresAt: string;
}

async function parseError(response: Response): Promise<string> {
  try {
    const body = await response.json() as { message?: string };
    return body.message || `Request failed with status ${response.status}`;
  } catch {
    return `Request failed with status ${response.status}`;
  }
}

// A null token means the console session lives in the httpOnly cookie;
// credentials plus the X-Limrun-Client header authenticate the request then.
function cliAuthHeaders(token: string | null): Record<string, string> {
  return {
    "X-Limrun-Client": "console",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function fetchCliSession(apiEndpoint: string, token: string | null, sessionId: string): Promise<CliSessionInfo> {
  const response = await fetch(
    new URL(`/authn/cli/sessions/${encodeURIComponent(sessionId)}`, apiEndpoint),
    {
      credentials: "include",
      headers: cliAuthHeaders(token),
    }
  );
  if (!response.ok) {
    throw new Error(await parseError(response));
  }
  return await response.json() as CliSessionInfo;
}

async function approveCliSession(
  apiEndpoint: string,
  token: string | null,
  sessionId: string,
  organizationId: string
): Promise<void> {
  const response = await fetch(
    new URL(`/authn/cli/sessions/${encodeURIComponent(sessionId)}/approve`, apiEndpoint),
    {
      method: "POST",
      credentials: "include",
      headers: {
        ...cliAuthHeaders(token),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ organizationId }),
    }
  );
  if (!response.ok) {
    throw new Error(await parseError(response));
  }
}

async function sendTokenToCli(token: string): Promise<void> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), CLI_CALLBACK_TIMEOUT_MS);
  try {
    const cliResponse = await fetch(
      `http://localhost:${CLI_SERVER_PORT}/authn/callback?api-key=${encodeURIComponent(token)}`,
      {
        method: "GET",
        signal: controller.signal,
      }
    );

    if (!cliResponse.ok) {
      throw new Error(`CLI server responded with status ${cliResponse.status}`);
    }
  } finally {
    window.clearTimeout(timeout);
  }
}

export default function CLILogin() {
  const auth = useAuth();
  const { user } = useUser();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session") || "";
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [deliveryState, setDeliveryState] = useState<CliDeliveryState>("idle");
  const [generatedToken, setGeneratedToken] = useState<string>("");
  const [tokenOrgId, setTokenOrgId] = useState<string>("");
  const [copied, setCopied] = useState(false);
  const [showToken, setShowToken] = useState(false);
  // Ref guards the auto-auth effect so re-renders never re-trigger it; the
  // display flag is state only for rendering.
  const autoAuthTriggered = useRef(false);
  const deliveryAttempt = useRef(0);
  const deliveryStateRef = useRef<CliDeliveryState>("idle");
  const [isAutoSelectingDisplay, setIsAutoSelectingDisplay] = useState(false);
  const [sessionInfo, setSessionInfo] = useState<CliSessionInfo | null>(null);
  const [sessionLoading, setSessionLoading] = useState(Boolean(sessionId));
  const [sessionError, setSessionError] = useState<string>("");
  const [sessionApproved, setSessionApproved] = useState(false);

  const updateDeliveryState = (state: CliDeliveryState) => {
    deliveryStateRef.current = state;
    setDeliveryState(state);
  };

  const deliverTokenToCli = async (token: string) => {
    if (deliveryStateRef.current === "connected" || deliveryStateRef.current === "delivering") {
      return;
    }
    const attempt = deliveryAttempt.current + 1;
    deliveryAttempt.current = attempt;
    updateDeliveryState("delivering");
    try {
      await sendTokenToCli(token);
      if (deliveryAttempt.current !== attempt) {
        return;
      }
      updateDeliveryState("connected");
      toast({
        title: "Authorization successful!",
        description: "You can now close this window and return to your CLI.",
      });
    } catch {
      if (deliveryAttempt.current !== attempt) {
        return;
      }
      updateDeliveryState("manual");
      setShowToken(true);
      toast({
        title: "Token created successfully",
        description: "Allow the browser permission prompt if needed, then retry the CLI connection.",
        action: (
          <ToastAction altText="Retry CLI connection" onClick={() => void deliverTokenToCli(token)}>
            Retry
          </ToastAction>
        ),
      });
    }
  };

  const handleRetryCliConnection = () => {
    if (!generatedToken || deliveryState === "delivering") {
      return;
    }
    void deliverTokenToCli(generatedToken);
  };

  const handleAuthorizeWithOrgId = async (orgId: string) => {
    if (!orgId) {
      toast({
        title: "Please select an organization",
        variant: "destructive",
      });
      return;
    }

    setIsAuthorizing(true);
    updateDeliveryState("idle");
    setGeneratedToken("");
    setTokenOrgId("");
    try {
      if (sessionId) {
        await approveCliSession(auth.apiEndpoint, auth.token, sessionId, orgId);
        setTokenOrgId(orgId);
        setSessionApproved(true);
        toast({
          title: "Authorization approved",
          description: "Return to your terminal to finish logging in.",
        });
        return;
      }

      const response = await auth.client.POST("/v1/organizations/{organizationId}/api_keys", {
        params: {
          path: { organizationId: orgId },
        },
        body: {
          description: `CLI Token - ${new Date().toISOString().split('T')[0]}`,
          expirationMonths: 12, // 1 year expiration for CLI tokens
        },
      });

      if (response.error || !response.data?.token) {
        throw new Error(response.error?.message || "Failed to create token");
      }

      const token = response.data.token;

      setGeneratedToken(token);
      setTokenOrgId(orgId);

      await deliverTokenToCli(token);

    } catch (error) {
      toast({
        title: "Authorization failed",
        description: error instanceof Error ? formatErrorMessage(error.message) : "An unexpected error occurred.",
        variant: "destructive",
      });
      // Keep `autoAuthTriggered` set so the effect doesn't re-fire when
      // `isAuthorizing` flips back to false; retries go through the manual
      // button, which bypasses the effect.
      setIsAutoSelectingDisplay(false);
      updateDeliveryState("idle");
    } finally {
      setIsAuthorizing(false);
    }
  };

  const handleAuthorize = () => {
    handleAuthorizeWithOrgId(selectedOrgId);
  };

  // Auto-authorize when the user has exactly one organization. Depend on the
  // id primitive, not the `user` object, since `useUser` returns a new
  // reference on every render.
  const defaultOrgId =
    user !== "loading" && user
      ? user.defaultOrganization?.id || user.organizations[0]?.id
      : undefined;
  const singleOrgId =
    user !== "loading" && user && user.organizations.length === 1
      ? user.organizations[0].id
      : undefined;

  useEffect(() => {
    if (!sessionId) {
      setSessionInfo(null);
      setSessionError("");
      setSessionLoading(false);
      return;
    }

    let cancelled = false;
    setSessionLoading(true);
    setSessionError("");
    void fetchCliSession(auth.apiEndpoint, auth.token, sessionId)
      .then((info) => {
        if (cancelled) {
          return;
        }
        setSessionInfo(info);
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        setSessionError(error instanceof Error ? formatErrorMessage(error.message) : "Failed to load CLI session.");
      })
      .finally(() => {
        if (!cancelled) {
          setSessionLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [auth.apiEndpoint, auth.token, sessionId]);

  useEffect(() => {
    if (sessionId && defaultOrgId && !selectedOrgId) {
      setSelectedOrgId(defaultOrgId);
    }
  }, [defaultOrgId, sessionId, selectedOrgId]);

  useEffect(() => {
    if (
      sessionId ||
      !singleOrgId ||
      isAuthorizing ||
      deliveryState !== "idle" ||
      autoAuthTriggered.current
    ) {
      return;
    }
    autoAuthTriggered.current = true;
    setSelectedOrgId(singleOrgId);
    setIsAutoSelectingDisplay(true);
    void handleAuthorizeWithOrgId(singleOrgId);
  }, [singleOrgId, isAuthorizing, deliveryState]);

  // If user is not logged in, redirect to login
  if (user === "loading") {
    return (
      <PublicShell>
        <ReloadIcon className="h-8 w-8 animate-spin" />
      </PublicShell>
    );
  }

  if (!user || user.organizations.length === 0) {
    sessionStorage.setItem("returnTo", window.location.pathname + window.location.search);
    return <Navigate to="/authn/login" replace />;
  }

  const tokenOrg = user.organizations.find(org => org.id === tokenOrgId);
  const renderOrganizationSelector = () => (
    <div className="space-y-2">
      <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
        {user.organizations.length === 1 ? "Organization" : "Select Organization"}
      </label>
      {user.organizations.length === 1 ? (
        <div className="flex items-center gap-2 p-3 rounded-md border bg-muted">
          <CheckCircledIcon className="h-4 w-4 text-success" />
          <span>{user.organizations[0].displayName || user.organizations[0].id}</span>
          {isAutoSelectingDisplay && (
            <span className="ml-auto text-xs text-muted-foreground flex items-center gap-1">
              <ReloadIcon className="h-3 w-3 animate-spin" />
              Auto-authorizing...
            </span>
          )}
        </div>
      ) : (
        <Select
          value={selectedOrgId}
          onValueChange={setSelectedOrgId}
          disabled={isAuthorizing || deliveryState === "delivering"}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select an organization" />
          </SelectTrigger>
          <SelectContent>
            {user.organizations.map((org) => (
              <SelectItem key={org.id} value={org.id}>
                {org.displayName || org.id}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );

  if (sessionId) {
    if (sessionLoading) {
      return (
        <PublicShell>
          <Card variant="plain" className="w-full max-w-lg">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">Loading CLI Session</CardTitle>
              <CardDescription>Checking the pending CLI login request.</CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center">
              <ReloadIcon className="h-8 w-8 animate-spin" />
            </CardContent>
          </Card>
        </PublicShell>
      );
    }

    if (sessionError || !sessionInfo) {
      return (
        <PublicShell>
          <Card variant="plain" className="w-full max-w-lg">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">CLI Session Unavailable</CardTitle>
              <CardDescription>
                {sessionError || "This CLI login session could not be found."}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => window.location.href = "/"} className="w-full" variant="outline">
                Go to dashboard
              </Button>
            </CardContent>
          </Card>
        </PublicShell>
      );
    }

    if (sessionApproved) {
      return (
        <PublicShell>
          <Card variant="plain" className="w-full max-w-lg">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl text-green-600">Authorization Complete</CardTitle>
              <CardDescription>
                Return to your terminal to finish logging in.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-center text-sm text-muted-foreground">
                Your CLI is now approved for <strong>{tokenOrg?.displayName || tokenOrg?.id}</strong>.
              </p>
              <Button onClick={() => window.close()} className="w-full" variant="outline">
                Close Window
              </Button>
            </CardContent>
          </Card>
        </PublicShell>
      );
    }

    return (
      <PublicShell>
        <Card variant="plain" className="w-full max-w-lg">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Is this you?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">{sessionInfo.hostname}</strong> wants to retrieve an API key.
              </p>
            </div>

            {renderOrganizationSelector()}

            <Button
              onClick={handleAuthorize}
              disabled={isAuthorizing || !selectedOrgId}
              className="w-full"
            >
              {isAuthorizing && (
                <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
              )}
              {isAuthorizing ? "Authorizing..." : "Approve CLI Session"}
            </Button>

            <Button
              variant="link"
              onClick={() => window.location.href = "/"}
              className="w-full text-sm text-muted-foreground"
            >
              Cancel and go to dashboard
            </Button>

            <p className="text-center text-xs text-muted-foreground">
              Phrase: <span className="font-mono">{sessionInfo.phrase}</span>
            </p>
          </CardContent>
        </Card>
      </PublicShell>
    );
  }

  const showDeliveryResult = deliveryState !== "idle" && Boolean(generatedToken);

  if (showDeliveryResult) {
    const isConnected = deliveryState === "connected";
    const isDelivering = deliveryState === "delivering";
    return (
      <PublicShell>
        <Card variant="plain" className="w-full max-w-lg">
          <CardHeader className="text-center">
            <CardTitle className={`text-2xl ${isConnected ? "text-green-600" : ""}`}>
              {isConnected ? "Authorization Complete" : isDelivering ? "Connecting CLI" : "API Key Created"}
            </CardTitle>
            <CardDescription>
              {isConnected
                ? "You can now close this window and return to your CLI."
                : "Your API key was created, but this page has not connected it to your local CLI yet."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="text-center space-y-2">
              <p className="text-sm text-muted-foreground">
                {isConnected ? (
                  <>
                    Your CLI is now connected to <strong>{tokenOrg?.displayName || tokenOrg?.id}</strong>.
                  </>
                ) : isDelivering ? (
                  "Sending the API key to your local CLI..."
                ) : (
                  "Keep `lim login` running, allow the browser permission prompt if shown, then retry the CLI connection. If retry still fails, copy the API key below and use it manually."
                )}
              </p>
            </div>

            {generatedToken && (
              <div className="space-y-2">
                <Button
                  variant="outline"
                  onClick={() => setShowToken(!showToken)}
                  className="w-full justify-between p-3 h-auto"
                >
                  <span className="text-sm font-medium">
                    API Key
                  </span>
                  {showToken ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
                </Button>

                {showToken && (
                  <div className="flex gap-2 animate-in slide-in-from-top-2 duration-200">
                    <div className="flex-1 p-3 bg-muted rounded-md border font-mono text-xs break-all">
                      {generatedToken}
                    </div>
                    <TooltipProvider>
                      <Tooltip open={copied}>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => {
                              navigator.clipboard.writeText(generatedToken);
                              setCopied(true);
                              setTimeout(() => setCopied(false), 2000);
                            }}
                          >
                            {copied ? <CheckIcon className="h-4 w-4" /> : <CopyIcon className="h-4 w-4" />}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {copied ? "Copied!" : "Copy token"}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                )}
              </div>
            )}

            {!isConnected && (
              <Button
                onClick={handleRetryCliConnection}
                disabled={isDelivering}
                className="w-full"
              >
                {isDelivering && (
                  <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isDelivering ? "Connecting CLI..." : "Retry CLI connection"}
              </Button>
            )}

            <Button
              onClick={() => window.close()}
              className="w-full"
              variant="outline"
            >
              Close Window
            </Button>
          </CardContent>
        </Card>
      </PublicShell>
    );
  }

  return (
    <PublicShell>
      <Card variant="plain" className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Authorize CLI Access</CardTitle>
          <CardDescription>
            Select an organization to authorize your CLI tool.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {renderOrganizationSelector()}

          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              This will create an API key and configure your CLI to use it for accessing the selected organization.
            </p>
          </div>

          {!isAutoSelectingDisplay && (
            <>
              <Button
                onClick={handleAuthorize}
                disabled={isAuthorizing || deliveryState === "delivering" || !selectedOrgId}
                className="w-full"
              >
                {isAuthorizing && (
                  <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isAuthorizing ? "Authorizing..." : "Authorize CLI"}
              </Button>

              <div className="text-center">
                <Button
                  variant="link"
                  onClick={() => window.location.href = "/"}
                  className="text-sm text-muted-foreground"
                >
                  Cancel and go to dashboard
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </PublicShell>
  );
}
